import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { useNewsArticle } from "../../hooks/NewsHooks";
import Page from "../../layout/Page";
import Herobox from "../../components/Herobox";
import ContentSection from "../../components/ContentSection";
import NotFoundPage from "../NotFoundPage";
import "./style.scss";
import { CockpitNewsGrid } from "../../components/NewsGrid";
import Breadcrumbs from "../../components/Breadcrumbs";
import { homeUrl, newsBaseUrl } from "../../config/urls";
import { transformHtml } from "../../utils/CmsUtils";
import { JsonLd } from "react-schemaorg";
import { Helmet } from "react-helmet";
import Icon from "../../components/Icon";
import { useLocSetting } from "../../hooks/SettingsHooks";
import prettyBytes from "pretty-bytes";
import BjelinButton from "../../components/BjelinButton";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import axios from "axios";
import logo from "../../assets/bjelin-logo.png";

const NewsArticlePage = ({ newsId }) => {
  const article = useNewsArticle(newsId);
  const newsFooter = useLocSetting(["content", "news_footer"]);

  const t = useBjelinTranslations();

  if (!article) {
    return <NotFoundPage />;
  }

  const breadcrumbItems = [
    { url: homeUrl, text: t("home") },
    { url: newsBaseUrl, text: t("news") }
  ];

  const cdnClient = axios.create({ responseType: "blob" });

  const downloadZipFile = async () => {
    const zip = new JSZip();
    try {
      await Promise.all(
        article.media.map(async asset => {
          const res = await cdnClient.get(asset.path);
          if (res.data) {
            return zip.file(asset.title, res.data, { binary: true });
          }
        })
      );
      //console.log("zip files:", zip.files);
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "Bjelin.zip");
    } catch (err) {
      alert(t("error.general"));
    }
  };

  return (
    <>
      <JsonLd
        item={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: article.heading,
          image: article.image.path,
          datePublished: article.publishedAt
        }}
      />
      <Helmet>
        <meta property="og:site_name" content={"Bjelin"} />
        <meta property="og:type" content={"article"} />
        <meta property="og:title" content={article.heading} />
        <meta
          property="og:article:published_time"
          content={article.publishedAt}
        />
        {article.summary && (
          <meta
            property="og:description"
            name="description"
            content={article.summary}
          />
        )}
        <meta property="og:image" content={article.image.path} />
        {/*<meta property="fb:app_id" content={} />*/}
      </Helmet>
      <Page
        name="news-article-page"
        key="news-article-page"
        title={article.heading}
      >
        <Herobox
          type="fat"
          textShadow={true}
          className={"text-center"}
          backgroundImage={article.image?.styles?.full || article.image}
          centerImage={true}
        >
          <h1>{article.heading}</h1>
          <div className="image-caption">{article.imageCaption}</div>
        </Herobox>
        <ContentSection>
          <div className="news-wrapper">
            <Breadcrumbs items={breadcrumbItems} />
            <div className="news-meta">
              <div className="news-date">
                {article.publishedAt}
                {" | "}
                {article.typeOfMedia}
              </div>
            </div>

            <div>{transformHtml(article.body)}</div>

            {article.url && (
              <div className="news-link">
                <a href={article.url} target="_blank" rel="noopener noreferrer">
                  {t("news.source.link")}{" "}
                  <Icon name="arrow-up-right-from-square" />
                </a>
              </div>
            )}
            {newsFooter && (
              <div
                className={"news-footer"}
                dangerouslySetInnerHTML={{
                  __html: newsFooter
                }}
              />
            )}
            {Array.isArray(article.contactPeople) && (
              <>
                <h3 className="contacts-heading">{t("news.contacts")}</h3>
                <div className="contacts">
                  {article.contactPeople.map(person => (
                    <div key={person.name} className="contact-person">
                      <img
                        className="contact-image"
                        src={
                          person?.image?.styles?.preview ||
                          person?.image?.path ||
                          logo
                        }
                        alt={person.name}
                      />
                      <h4 className="contact-name">{person.name}</h4>
                      <div className="contact-title">{person.title}</div>
                      <div className="contact-email">
                        <a href={"mailto:" + person.email}>{person.email}</a>
                      </div>
                      {person.phone && (
                        <div className="contact-phone">
                          <a href={"tel:" + person.phone}>{person.phone}</a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
            {article?.media?.length > 0 && (
              <div className={"news-media"}>
                <h3>{t("news.media")}</h3>
                <BjelinButton color={"black"} onClick={() => downloadZipFile()}>
                  {t("news.download.all.images")}
                </BjelinButton>
                <ul>
                  {article.media.map((asset, idx) => {
                    let icon;
                    if (asset.image) {
                      icon = "image";
                    } else if (asset.archive) {
                      icon = "file-zipper";
                    } else {
                      icon = "file";
                    }
                    return (
                      <li key={idx}>
                        <a href={asset.path}>
                          <Icon name={icon} /> {asset.title} (
                          {prettyBytes(asset.size)})
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </ContentSection>
        <ContentSection theme="smoke">
          <h2 style={{ margin: "3rem 2rem 2rem" }}>{t("news.heading.more")}</h2>
          <CockpitNewsGrid limit={100} />
        </ContentSection>
      </Page>
    </>
  );
};

export default NewsArticlePage;
